import { orderBy, query, updateDoc } from 'firebase/firestore';

import { getAppBroker } from 'stores/AppBroker';
import { DocumentStatus } from 'types/DocumentStatus';
import { arrayMaxBy } from 'utils/Array';
import { nowTimestamp, timestampCompare } from 'utils/FirestoreUtils';

import type Ad from './Ad';
import Comment, { type CommentSchema } from './Comment';
import DocumentCollection from './DocumentCollection';
import type DocumentStore from './DocumentStore';
import type TearsheetAd from './TearsheetAd';

class CommentsManager extends DocumentCollection<Comment, CommentSchema> {
    status: DocumentStatus = DocumentStatus.NONE;
    query = () => query(this.collectionRef, orderBy('timestamp'));

    editing = '';
    collapsed = true;
    parent: DocumentStore<any>;
    enabled = false;

    constructor(parent: DocumentStore<any>) {
        super(parent, Comment);
        this.parent = parent;
        this.becomeObservable();
    }

    get parentDoc() {
        return this.parent as Ad | Animation | TearsheetAd;
    }

    get all() {
        const comments = [...this.children];
        comments.sort((a, b) =>
            timestampCompare(a.data.timestamp, b.data.timestamp)
        );
        return comments;
    }

    get newCommentsCount() {
        const lastViewTimestamp = this.getLastViewTimestamp();
        if (!lastViewTimestamp) return this.children.length;
        // Ensure lastViewTimestamp is valid
        if (typeof lastViewTimestamp !== 'number') return this.children.length;

        // Helper function to safely get timestamp seconds
        const getTimestampSeconds = (timestamp: any): number | null => {
            if (timestamp && typeof timestamp.seconds === 'number') {
                return timestamp.seconds;
            }
            return null;
        };

        // Update the filter to use the helper function and ignore malformed timestamps
        return this.children.filter(comment => {
            const commentTimestamp = getTimestampSeconds(comment.data.timestamp);
            // If the timestamp is malformed (null), we ignore it
            if (commentTimestamp === null) return false;
            return commentTimestamp > lastViewTimestamp;
        }).length;
    }

    get newest() {
        return arrayMaxBy(this.children, (a, b) =>
            timestampCompare(a.data.timestamp, b.data.timestamp)
        );
    }

    enable = () => {
        this.enabled = true;
    }

    disable = () => {
        this.enabled = false;
    }

    updateCommentsDatetime() {
        for (const comment of this.children) {
            comment.updateDatetime();
        }
    }

    getComment(uid: string) {
        for (const comment of this.children) {
            if (comment.uid === uid) {
                return comment;
            }
        }
    }

    addComment = async (content: string) => {
        const user = getAppBroker().getCurrentUser();
        if (!user) return;

        const placeholderName = user.placeholderName;

        const identity = placeholderName
            ? { placeholderName }
            : { owner: user.id };

        const updateP = placeholderName
            ? Promise.resolve()
            : this.updateViewHistory();

        const addP = this.add({
            timestamp: nowTimestamp(),
            lastModified: nowTimestamp(),
            content,
            ...identity,
        });

        await Promise.all([updateP, addP]);
    };

    updateViewHistory = async () => {
        const user = getAppBroker().getCurrentUser();
        if (!user) return;

        // patch view history in db
        const operation = updateDoc(this.parent.docRef, {
            [`viewHistory.${user.id}`]: nowTimestamp(),
        });

        // update local view history. Every parent schema (Ad, Animation,
        // TearsheetAd) declares viewHistory; `|| {}` only covers a stored null.
        const viewHistory = this.parent.data.viewHistory || {};
        this.parent.data.viewHistory = {
            ...viewHistory,
            [user.id]: nowTimestamp(),
        };

        await operation;
    };

    delete = async (uid: string) => {
        const comment = this.getComment(uid);
        if (comment) {
            await comment.delete();
        }
    }

    reset() {
        this.status = DocumentStatus.NONE;
        this.editing = '';
        this.collapsed = true;
        super.reset();
    }

    getLastViewTimestamp() {
        const user = getAppBroker().getCurrentUser();
        const uid = user?.id;
        const viewHistory = this.parent.data.viewHistory;
        if (!viewHistory || !uid || !viewHistory[uid]) return null;
        return viewHistory[uid].seconds;
    }
}

export default CommentsManager;
